import React, { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, type SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "@components/Button";
import { SquarePlusIcon } from "@components/icons";
import { FormInput } from "@components/inputs/form/FormInput/FormInput";
import { Modal } from "@components/Modal/Modal";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useModal } from "@hooks/useModal";
import { type IUseModalProps } from "@store/modal/modal";
import { useCreateCustomTransactionCategoryMutation } from "@services/budget/mutations";
import { isNullOrUndefined } from "@utils/common";
import {
  useAddBudgetTransactionCategorySchema,
  type TAddBudgetTransactionCategorySchema,
} from "./useAddBudgetCategorySchema";

interface IAddBudgetTransactionCategoryModalProps extends IUseModalProps {
  onSuccess?: (transactionCategoryId: number) => void;
}

export const AddBudgetTransactionCategoryModal: React.FC<
  IAddBudgetTransactionCategoryModalProps
> = (props) => {
  const { onSuccess, ...rest } = props;
  const modal = useModal(rest);
  const { t } = useTranslation();

  const schema = useAddBudgetTransactionCategorySchema();

  const { control, handleSubmit, setError } =
    useForm<TAddBudgetTransactionCategorySchema>({
      resolver: zodResolver(schema),
    });

  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);

  const { toastApiError } = useApiErrorUtils(setError);
  const { mutateAsync, isPending } =
    useCreateCustomTransactionCategoryMutation();

  const onSubmit: SubmitHandler<TAddBudgetTransactionCategorySchema> = async (
    data,
  ) => {
    const response = await mutateAsync({ categoryName: data.name }).catch(
      toastApiError,
    );

    if (!isNullOrUndefined(response)) {
      onSuccess?.(response.transactionCategoryId);
      setIsSubmitSuccessful(true);
    }
  };

  return (
    <Modal
      modal={modal}
      badgeProps={
        isSubmitSuccessful
          ? undefined
          : {
              icon: SquarePlusIcon,
            }
      }
      title={
        !isSubmitSuccessful &&
        t("budget.addBudgetTransactionCategoryModal.title")
      }
      subtitle={
        !isSubmitSuccessful &&
        t("budget.addBudgetTransactionCategoryModal.subtitle")
      }
      actionButtons={
        <React.Fragment>
          {!isSubmitSuccessful && (
            <React.Fragment>
              <Button
                variant="secondary"
                className="w-full"
                onClick={modal.close}
              >
                {t("budget.addBudgetTransactionCategoryModal.cancel")}
              </Button>
              <Button
                className="w-full"
                onClick={handleSubmit(onSubmit)}
                isLoading={isPending}
              >
                {t("budget.addBudgetTransactionCategoryModal.submit")}
              </Button>
            </React.Fragment>
          )}
          {isSubmitSuccessful && (
            <Button
              className="w-full"
              variant="secondary"
              onClick={modal.close}
            >
              {t("budget.addBudgetTransactionCategoryModal.close")}
            </Button>
          )}
        </React.Fragment>
      }
    >
      {!isSubmitSuccessful && (
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormInput
            control={control}
            name="name"
            label={t("budget.addBudgetTransactionCategoryModal.name.label")}
            placeholder={t(
              "budget.addBudgetTransactionCategoryModal.name.placeholder",
            )}
          />
        </form>
      )}
      {isSubmitSuccessful && (
        <div className="flex flex-col items-center px-6 text-center">
          <img
            src="/images/budget/add-category-success.png"
            className="size-52 object-cover"
          />
          <h2 className="mt-2 text-lg font-semibold">
            {t("budget.addBudgetTransactionCategoryModal.success.title")}
          </h2>
          <span className="mt-4 text-sm text-grey-dark">
            {t("budget.addBudgetTransactionCategoryModal.success.subtitle")}
          </span>
        </div>
      )}
    </Modal>
  );
};
